import React from 'react';
import { FaCheckCircle, FaExclamationTriangle, FaTimesCircle, FaThermometerHalf, FaDollarSign, FaFlask } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';

const LiveCheckPanel = ({ liveData, loading }) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-cyan-50 to-blue-50 border border-cyan-200 rounded-xl p-4">
        <div className="flex items-center gap-2">
          <div className="animate-spin h-5 w-5 border-2 border-cyan-500 border-t-transparent rounded-full"></div>
          <span className="text-sm text-gray-700">Live SlushCheck™ analyserer...</span>
        </div>
      </div>
    );
  }

  if (!liveData) {
    return null;
  }

  const {
    brix,
    alcohol_percent,
    freeze_point_c,
    freeze_consistency,
    machine_compatibility,
    cost,
    warnings = [],
    errors = [],
    suggestions = []
  } = liveData;

  // Get consistency color
  const getConsistencyColor = (consistency) => {
    switch (consistency) {
      case 'optimal':
        return 'text-green-600';
      case 'soft':
        return 'text-yellow-600';
      case 'hard':
        return 'text-blue-600';
      case 'unstable':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  // Overall status
  const hasErrors = errors.length > 0 || (machine_compatibility && !machine_compatibility.ok);
  const hasWarnings = warnings.length > 0 || (machine_compatibility && machine_compatibility.warnings?.length > 0);
  const overallStatus = hasErrors ? 'error' : hasWarnings ? 'warning' : 'ok';

  return (
    <div className="bg-gradient-to-br from-cyan-50 to-blue-50 border-l-4 border-cyan-500 rounded-r-xl p-5 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
          <span className="text-2xl">⚡</span>
          {t('live.title', 'Live SlushCheck™')}
        </h3>
        {overallStatus === 'ok' && (
          <FaCheckCircle className="text-green-600 text-2xl" />
        )}
        {overallStatus === 'warning' && (
          <FaExclamationTriangle className="text-yellow-600 text-2xl" />
        )}
        {overallStatus === 'error' && (
          <FaTimesCircle className="text-red-600 text-2xl" />
        )}
      </div>

      {/* Quick Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {/* Brix */}
        <div className="bg-white/80 backdrop-blur rounded-lg p-3 text-center">
          <div className="flex items-center justify-center gap-1 text-xs text-gray-600 mb-1">
            <FaFlask className="text-cyan-500" />
            <span>{t('live.brix', 'Brix')}</span>
          </div>
          <div className="text-xl font-bold text-gray-800">{brix?.toFixed(1)}°Bx</div>
        </div>

        {/* Alcohol */}
        {alcohol_percent > 0 && (
          <div className="bg-white/80 backdrop-blur rounded-lg p-3 text-center">
            <div className="text-xs text-gray-600 mb-1">{t('live.alcohol', 'Alkohol')}</div>
            <div className="text-xl font-bold text-gray-800">{alcohol_percent?.toFixed(1)}%</div>
          </div>
        )}

        {/* Freeze Point */}
        <div className="bg-white/80 backdrop-blur rounded-lg p-3 text-center">
          <div className="flex items-center justify-center gap-1 text-xs text-gray-600 mb-1">
            <FaThermometerHalf className="text-blue-500" />
            <span>{t('live.freeze_point', 'Frysepunkt')}</span>
          </div>
          <div className="text-xl font-bold text-gray-800">{freeze_point_c?.toFixed(1)}°C</div>
        </div>

        {/* Consistency */}
        <div className="bg-white/80 backdrop-blur rounded-lg p-3 text-center">
          <div className="text-xs text-gray-600 mb-1">{t('live.consistency', 'Konsistens')}</div>
          <div className={`text-base font-bold capitalize ${getConsistencyColor(freeze_consistency)}`}>
            {freeze_consistency || 'N/A'}
          </div>
        </div>

        {/* Cost (if available) */}
        {cost && cost.per_200ml && (
          <div className="bg-white/80 backdrop-blur rounded-lg p-3 text-center">
            <div className="flex items-center justify-center gap-1 text-xs text-gray-600 mb-1">
              <FaDollarSign className="text-green-500" />
              <span>{t('live.cost', 'Pris/portion')}</span>
            </div>
            <div className="text-xl font-bold text-gray-800">{cost.per_200ml?.toFixed(2)} kr</div>
          </div>
        )}
      </div>

      {/* Machine Compatibility */}
      {machine_compatibility && (
        <div className={`rounded-lg p-3 ${
          machine_compatibility.ok 
            ? 'bg-green-100/50 border border-green-300' 
            : 'bg-red-100/50 border border-red-300'
        }`}>
          <div className="flex items-center gap-2 text-sm font-semibold mb-2">
            {machine_compatibility.ok ? (
              <>
                <FaCheckCircle className="text-green-600" />
                <span className="text-green-800">Maskinkompatibel</span>
              </>
            ) : (
              <>
                <FaTimesCircle className="text-red-600" />
                <span className="text-red-800">{t('live.machine_issues', 'Maskinproblemer')}</span>
              </>
            )}
          </div>
          {machine_compatibility.issues && machine_compatibility.issues.length > 0 && (
            <ul className="text-xs space-y-1 ml-5">
              {machine_compatibility.issues.map((issue, idx) => (
                <li key={idx} className="text-red-700">• {issue}</li>
              ))}
            </ul>
          )}
          {machine_compatibility.warnings && machine_compatibility.warnings.length > 0 && (
            <ul className="text-xs space-y-1 ml-5">
              {machine_compatibility.warnings.map((warning, idx) => (
                <li key={idx} className="text-yellow-700">• {warning}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Global Errors */}
      {errors.length > 0 && (
        <div className="bg-red-100/50 border border-red-300 rounded-lg p-3">
          <p className="text-sm font-semibold text-red-800 mb-2">{t('live.errors', 'Fejl')}:</p>
          <ul className="text-xs space-y-1 ml-4">
            {errors.map((error, idx) => (
              <li key={idx} className="text-red-700">• {error}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Global Warnings */}
      {warnings.length > 0 && (
        <div className="bg-yellow-100/50 border border-yellow-300 rounded-lg p-3">
          <p className="text-sm font-semibold text-yellow-800 mb-2">{t('live.warnings', 'Advarsler')}:</p>
          <ul className="text-xs space-y-1 ml-4">
            {warnings.map((warning, idx) => (
              <li key={idx} className="text-yellow-700">• {warning}</li>
            ))}
          </ul>
        </div>
      )}

      {/* AI Suggestions */}
      {suggestions.length > 0 && (
        <div className="bg-blue-100/50 border border-blue-300 rounded-lg p-3">
          <p className="text-sm font-semibold text-blue-800 mb-2">{t('live.suggestions', 'AI Forslag')}:</p>
          <ul className="text-xs space-y-1 ml-4">
            {suggestions.map((suggestion, idx) => (
              <li key={idx} className="text-blue-700">💡 {suggestion}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default LiveCheckPanel;
